import TabsWithOutlet from '../../layouts/components/TabsWithOutlet';
import React from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { DataManagementTab } from '@shared/components/data-management';
import { useConfirmation } from '@shared/components/confirmation';
import { useRemoveSubjectUser } from './hooks/useSubjectUsers';
import { createSubjectUsersConfig } from './config/subjectUsersConfig';
import { SubjectRouteParams } from './types';
import { SubjectSettingsTab } from './components/SubjectSettingsTab';
import { Box, Typography } from '@mui/material';
import { useBreadcrumbManager } from '../../hooks/useBreadcrumbManager';
import { useAuthStore } from '../authentication';
import { UserRole } from '../authentication/types';
import { useContextStore } from '../../stores/contextStore';

// Subject users component using the enhanced config
const SubjectUsersTab: React.FC = () => {
  const { tenantId, domainId, subjectId } = useParams<SubjectRouteParams>();
  const { confirm } = useConfirmation();
  const removeSubjectUserMutation = useRemoveSubjectUser(tenantId!, domainId!, subjectId!);
  const subjectUsersConfig = createSubjectUsersConfig(
    tenantId!,
    domainId!,
    subjectId!,
    removeSubjectUserMutation,
    confirm
  );

  return <DataManagementTab config={subjectUsersConfig} />;
};

// Main subject routes component
export const SubjectRoutes: React.FC = () => {
  const { tenantId, domainId, subjectId } = useParams<SubjectRouteParams>();

  // Initialize breadcrumb manager to automatically fetch and set subject names
  useBreadcrumbManager();

  // Get auth store values and context store for system admin context management
  const { currentTenant, userSession } = useAuthStore();
  const { setActiveTenantId, setActiveDomainId, setActiveSubjectId } = useContextStore();

  // System admins can access any tenant/domain/subject, others are restricted to their own
  const isSystemAdmin = userSession?.currentRoles?.includes(UserRole.SYSTEM_ADMIN);

  // For system admins, set the context to match the tenant, domain, and subject they're viewing
  React.useEffect(() => {
    if (isSystemAdmin && tenantId && domainId && subjectId) {
      // Set tenant context if different from current
      if (tenantId !== currentTenant?.id) {
        setActiveTenantId(tenantId);
      }
      // Always set domain and subject context for system admins
      setActiveDomainId(domainId);
      setActiveSubjectId(subjectId);
    }
  }, [
    isSystemAdmin,
    tenantId,
    domainId,
    subjectId,
    currentTenant?.id,
    setActiveTenantId,
    setActiveDomainId,
    setActiveSubjectId,
  ]);

  // Validate route parameters
  if (!tenantId || !domainId || !subjectId) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
          Invalid subject ID, institute ID, or university ID
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          Please select a valid subject from the list.
        </Typography>
      </Box>
    );
  }

  return (
    <Routes>
      <Route
        element={
          <TabsWithOutlet
            tabs={[
              {
                label: 'Users',
                href: `/app/manage/universities/${tenantId}/institutes/${domainId}/subjects/${subjectId}/users`,
              },
              {
                label: 'Settings',
                href: `/app/manage/universities/${tenantId}/institutes/${domainId}/subjects/${subjectId}/settings`,
              },
            ]}
          />
        }
      >
        <Route
          index
          element={
            <Navigate
              to={`/app/manage/universities/${tenantId}/institutes/${domainId}/subjects/${subjectId}/users`}
              replace
            />
          }
        />
        <Route path="users" element={<SubjectUsersTab />} />
        <Route path="settings" element={<SubjectSettingsTab />} />
      </Route>
    </Routes>
  );
};
